'use client';

import type { CreateApiKeyInput } from '@interfaces/apiKey';
import { Alert, Form, Input, Modal, Select } from 'antd';
import { useEffect } from 'react';

/**
 * Mirrors `keyNameSchema` in the API's key model. Validating here as well as
 * there is not belt-and-braces: the API's 400 arrives after the round trip, and
 * a name is the one thing the user can get wrong.
 */
const KEY_NAME_PATTERN = /^[\p{L}\p{N} ._\-()]+$/u;
const KEY_NAME_MAX_LENGTH = 64;

/** `MAX_KEY_LIFETIME_DAYS` is 365, so nothing longer is offered. */
const EXPIRY_OPTIONS = [
  { value: 0, label: 'Never expires (recommended)' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '365 days' },
];

type FormValues = {
  name: string;
  expiresInDays: number;
};

type Props = {
  open: boolean;
  isSubmitting: boolean;
  /** Message from a failed submit, already mapped to human copy by the caller. */
  errorMessage: string | null;
  onSubmit: (input: CreateApiKeyInput) => void;
  onCancel: () => void;
};

const CreateKeyModal: React.FC<Props> = ({ open, isSubmitting, errorMessage, onSubmit, onCancel }) => {
  const [form] = Form.useForm<FormValues>();

  // A reopened dialog starts clean — the previous name is never what the user
  // wants for a second key, and a stale value would look like it was saved.
  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleFinish = ({ name, expiresInDays }: FormValues) => {
    onSubmit({
      name: name.trim(),
      ...(expiresInDays > 0 ? { expiresInDays } : {}),
    });
  };

  return (
    <Modal
      open={open}
      title="Create an API key"
      okText="Create key"
      cancelText="Cancel"
      confirmLoading={isSubmitting}
      onOk={() => form.submit()}
      onCancel={onCancel}
      maskClosable={!isSubmitting}
      destroyOnHidden
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ expiresInDays: 0 }}
        onFinish={handleFinish}
        disabled={isSubmitting}
      >
        <Form.Item
          name="name"
          label="Key name"
          extra="Use one key per integration, so revoking one does not take down the others."
          rules={[
            { required: true, whitespace: true, message: 'Give the key a name so you can tell it apart later.' },
            { max: KEY_NAME_MAX_LENGTH, message: `Use at most ${KEY_NAME_MAX_LENGTH} characters.` },
            {
              pattern: KEY_NAME_PATTERN,
              message: 'Use letters, numbers, spaces and . _ - ( ) only.',
            },
          ]}
        >
          <Input placeholder="Production integration" autoComplete="off" maxLength={KEY_NAME_MAX_LENGTH} />
        </Form.Item>

        <Form.Item
          name="expiresInDays"
          label="Expiry"
          extra="An expiring key stops working on its own. Most integrations should not expire."
        >
          <Select options={EXPIRY_OPTIONS} />
        </Form.Item>
      </Form>

      {errorMessage !== null && (
        <Alert type="error" showIcon title="The key was not created" description={errorMessage} />
      )}
    </Modal>
  );
};

export { CreateKeyModal };
